<script>
import {useCartStore} from "@js/store/CartStore.js";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import CallbackErrorMessage from "@js/components/Statuses/CallbackErrorMessage.vue";
import {IMaskDirective} from "vue-imask";
import PrivacyPolicyAgreementLink from "@js/components/Sections/Links/PrivacyPolicyAgreementLink.vue";
import {computed} from "vue";
import SpinnerIndicator from "@js/components/Statuses/SpinnerIndicator.vue";
import IconX from "@js/components/Icons/IconX.vue";

export default {
    name: "CartForm",
    components: {
        IconX,
        SpinnerIndicator,
        PrivacyPolicyAgreementLink,
        CallbackErrorMessage,
        NumberFormatComponent,
    },
    directives: {imask: IMaskDirective},
    setup() {
        const cartStore = useCartStore();

        const productsSum = computed(() => cartStore.subTotal);
        const weightTotal = computed(() => cartStore.weightTotal);
        const total = computed(() => productsSum.value);

        const phoneMask = {mask: "+7 (000) 000-00-00"};
        const innMask = {mask: "000000000000"};

        const updatePhone = (val) => {
            cartStore.form.phone = val;
        };

        const updateInn = (val) => {
            cartStore.form.inn = val;
        };

        const sending = computed(() => !!cartStore.loadings?.sending);
        const showAddressHint = computed(() => cartStore.form.delivery_type !== 'self_delivery');
        const showInnField = computed(() => cartStore.form.payment_type === 'invoice');

        const onFilesChange = (event) => {
            cartStore.setFiles(event.target.files);
            event.target.value = '';
        };

        return {
            cartStore,
            productsSum,
            weightTotal,
            total,
            phoneMask,
            updatePhone,
            innMask,
            updateInn,
            sending,
            showAddressHint,
            showInnField,
            onFilesChange,
        };
    },
};
</script>

<template>
    <div class="flex flex-col bg-grayBg rounded-2xl p-4 gap-5">
        <div>
            <h2 class="text-lg font-medium mb-3">Предварительный расчёт</h2>

            <div v-if="weightTotal !== null" class="flex items-end justify-between w-full text-sm mb-2">
                <div class="font-medium">Общий вес</div>
                <div class="font-bold">
                    <NumberFormatComponent :number="weightTotal" append_text=" кг"/>
                </div>
            </div>

            <div class="flex items-end justify-between w-full">
                <div class="font-medium">Предварительная сумма</div>
                <div class="font-bold">
                    <NumberFormatComponent :number="total" append_text=" ₽"/>
                </div>
            </div>

            <p class="text-xs text-gray-500 mt-2 leading-relaxed">
                Итоговая стоимость зависит от засора, типа металла, объёма, необходимости демонтажа, погрузки и вывоза.
            </p>
        </div>

        <div class="flex flex-col gap-3">
            <h2 class="text-lg font-medium">Данные для заявки</h2>

            <div>
                <input
                    v-model="cartStore.form.full_name"
                    type="text"
                    placeholder="Ваше имя"
                    autocomplete="off"
                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"
                    :class="[cartStore.form_errors && cartStore.form_errors['full_name'] ? 'border-red-600' : '']"
                />
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'full_name'"/>
            </div>

            <div>
                <input
                    :value="cartStore.form.phone"
                    v-imask="phoneMask"
                    @keyup="updatePhone($event.target.value)"
                    type="tel"
                    placeholder="+7 (___) ___-__-__"
                    autocomplete="off"
                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"
                    :class="[cartStore.form_errors && cartStore.form_errors['phone'] ? 'border-red-600' : '']"
                />
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'phone'"/>
            </div>

<!--            <div>-->
<!--                <input-->
<!--                    v-model="cartStore.form.email"-->
<!--                    type="email"-->
<!--                    placeholder="Email"-->
<!--                    autocomplete="off"-->
<!--                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"-->
<!--                    :class="[cartStore.form_errors && cartStore.form_errors['email'] ? 'border-red-600' : '']"-->
<!--                />-->
<!--                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'email'"/>-->
<!--            </div>-->

<!--            <div v-if="showInnField">-->
<!--                <input-->
<!--                    :value="cartStore.form.inn"-->
<!--                    v-imask="innMask"-->
<!--                    @keyup="updateInn($event.target.value)"-->
<!--                    type="text"-->
<!--                    placeholder="ИНН*"-->
<!--                    autocomplete="off"-->
<!--                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"-->
<!--                    :class="[cartStore.form_errors && cartStore.form_errors['inn'] ? 'border-red-600' : '']"-->
<!--                />-->
<!--                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'inn'"/>-->
<!--            </div>-->

            <div class="mt-2">
                <textarea
                    v-model="cartStore.form.comment"
                    rows="5"
                    class="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border-2 border-gray-300 focus:ring-0 focus:border-baseColor"
                    placeholder="Комментарий к заявке: тип металла, примерный объём, нужен ли демонтаж, есть ли погрузка, фото, удобное время для звонка..."
                ></textarea>
            </div>

            <div class="mt-2">
                <div class="mb-2 text-sm font-medium text-gray-900">
                    Фото металлолома или объекта
                </div>

                <label
                    class="flex items-center justify-center w-full min-h-[120px] rounded-xl border-2 border-dashed border-baseColor/40 bg-white px-4 py-6 text-center cursor-pointer md:hover:border-baseColor transition"
                >
                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        class="hidden"
                        @change="onFilesChange"
                    />
                    <div>
                        <div class="font-medium text-gray-900">Загрузить фото</div>
                        <div class="text-sm text-gray-500 mt-1">
                            Можно прикрепить несколько изображений для более точной оценки
                        </div>
                    </div>
                </label>

                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'files'"/>
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'files.0'"/>
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'files.1'"/>
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'files.2'"/>
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'files.3'"/>
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'files.4'"/>

                <div v-if="cartStore.file_previews.length" class="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                    <div
                        v-for="(preview, index) in cartStore.file_previews"
                        :key="preview + '-' + index"
                        class="relative rounded-xl overflow-hidden border border-gray-200 bg-white"
                    >
                        <img
                            :src="preview"
                            :alt="`Фото ${index + 1}`"
                            class="w-full h-28 object-cover"
                        />
                        <button
                            type="button"
                            @click="cartStore.removeFile(index)"
                            class="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center shadow md:hover:bg-red-50 transition">
                            <IconX class="w-4 h-4"/>
                        </button>
                    </div>
                </div>
            </div>

            <div
                v-if="showAddressHint"
                class="rounded-xl border border-baseColor/20 bg-white p-3 text-sm text-gray-700 leading-relaxed"
            >
                После отправки заявки мы свяжемся с вами, уточним адрес, объём металлолома, условия подъезда и формат вывоза.
            </div>

            <div class="mt-2">
                <PrivacyPolicyAgreementLink/>
            </div>

            <div>
                <button
                    type="button"
                    @click.prevent="cartStore.sendForm"
                    :disabled="sending"
                    class="flex items-center justify-center w-full gap-2 leading-tight uppercase text-nowrap bg-baseColor md:hover:bg-baseColor/80 text-black focus:outline-none focus:ring-0 font-bold rounded-xl text-sm px-5 py-4 transition disabled:opacity-60 disabled:cursor-not-allowed"
                    aria-live="polite"
                >
                    <SpinnerIndicator v-if="sending" class="w-5 h-5"/>
                    {{ sending ? 'Отправляем…' : 'Отправить заявку' }}
                </button>
            </div>

            <p
                v-if="cartStore.cart_form_successfully_sent"
                class="text-green-700 text-sm mt-1"
                role="status"
            >
                Заявка отправлена! Мы свяжемся с вами в ближайшее время.
            </p>
        </div>
    </div>
</template>

<style scoped lang="scss">
</style>
